import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Task } from '@/server/modules/tasks/db/schema'
import type {
  CreateTaskInput,
  UpdateTaskInput,
  TasksQuery,
  TaskEntityType,
} from '@/shared/schemas/task.schema'
import { buildQueryString, fetchJson, fetchMutation } from '@/client/lib/api-helpers'
import type { ApiDeleteResponse } from '@/client/lib/api-types'

/**
 * TanStack Query hooks for tasks
 *
 * Provides data fetching and mutations with automatic cache management
 */

// Base URL for API requests
const API_BASE = '/api/tasks'

// Query keys for cache management
export const TASKS_KEYS = {
  all: ['tasks'] as const,
  lists: () => [...TASKS_KEYS.all, 'list'] as const,
  list: (filters?: TasksQuery) => [...TASKS_KEYS.lists(), filters] as const,
  entity: (entityType: TaskEntityType, entityId: string) =>
    [...TASKS_KEYS.all, 'entity', entityType, entityId] as const,
  today: () => [...TASKS_KEYS.all, 'today'] as const,
  details: () => [...TASKS_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...TASKS_KEYS.details(), id] as const,
}

// API response types
interface TasksResponse {
  tasks: Task[]
  count: number
}

interface TaskResponse {
  task: Task
}

interface TodayTasksResponse {
  tasks: Task[]
  counts: {
    overdue: number
    dueToday: number
    noDueDate: number
    total: number
  }
}

/**
 * Fetch tasks with optional filters
 */
export function useTasks(filters?: TasksQuery) {
  return useQuery({
    queryKey: TASKS_KEYS.list(filters),
    queryFn: async (): Promise<Task[]> => {
      const data = await fetchJson<TasksResponse>(
        `${API_BASE}${buildQueryString(filters)}`
      )
      return data.tasks
    },
  })
}

/**
 * Fetch tasks for a specific entity
 * Used in entity detail pages (TasksPanel)
 */
export function useEntityTasks(entityType: TaskEntityType, entityId: string) {
  const filterKey = `${entityType}Id` as keyof TasksQuery
  const filters: TasksQuery = { [filterKey]: entityId }

  return useQuery({
    queryKey: TASKS_KEYS.entity(entityType, entityId),
    queryFn: async (): Promise<Task[]> => {
      const data = await fetchJson<TasksResponse>(
        `${API_BASE}${buildQueryString(filters)}`
      )
      return data.tasks
    },
    enabled: !!entityId,
  })
}

/**
 * Fetch today's tasks (for dashboard widget)
 */
export function useMyTasksToday() {
  return useQuery({
    queryKey: TASKS_KEYS.today(),
    queryFn: async (): Promise<TodayTasksResponse> => {
      return fetchJson<TodayTasksResponse>(`${API_BASE}/today`)
    },
  })
}

/**
 * Fetch a single task by ID
 */
export function useTask(id: string) {
  return useQuery({
    queryKey: TASKS_KEYS.detail(id),
    queryFn: async (): Promise<Task> => {
      const data = await fetchJson<TaskResponse>(`${API_BASE}/${id}`)
      return data.task
    },
    enabled: !!id,
  })
}

/**
 * Create a new task
 */
export function useCreateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateTaskInput): Promise<Task> => {
      const data = await fetchMutation<TaskResponse>(API_BASE, 'POST', input)
      return data.task
    },
    onSuccess: () => {
      // Invalidate all task lists
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.today() })
    },
  })
}

/**
 * Update an existing task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateTaskInput
    }): Promise<Task> => {
      const result = await fetchMutation<TaskResponse>(
        `${API_BASE}/${id}`,
        'PUT',
        data
      )
      return result.task
    },
    onSuccess: (task) => {
      // Invalidate all task queries
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.today() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.detail(task.id) })
    },
  })
}

/**
 * Complete a task (quick action)
 */
export function useCompleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<Task> => {
      const data = await fetchMutation<TaskResponse>(
        `${API_BASE}/${id}/complete`,
        'POST'
      )
      return data.task
    },
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.today() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.detail(task.id) })
    },
  })
}

/**
 * Reopen a completed task
 */
export function useReopenTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<Task> => {
      const data = await fetchMutation<TaskResponse>(
        `${API_BASE}/${id}/reopen`,
        'POST'
      )
      return data.task
    },
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.today() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.detail(task.id) })
    },
  })
}

/**
 * Delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const data = await fetchMutation<ApiDeleteResponse>(
        `${API_BASE}/${id}`,
        'DELETE'
      )
      if (!data.success) {
        throw new Error('Delete operation failed')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: TASKS_KEYS.today() })
    },
  })
}

/**
 * Toggle task status between open and completed
 * Convenience hook for quick toggles
 */
export function useToggleTask() {
  const completeTask = useCompleteTask()
  const reopenTask = useReopenTask()

  return {
    isPending: completeTask.isPending || reopenTask.isPending,
    mutate: (task: Task) => {
      if (task.status === 'completed') {
        reopenTask.mutate(task.id)
      } else {
        completeTask.mutate(task.id)
      }
    },
  }
}
